import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and Shop Pay installments for the selected variant.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newProductPrice = this.#getMatchingProductPrice(event.detail.data.html);
    const newPrice = newProductPrice?.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (newPrice && currentPrice && currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }

    this.#updateInstallments(event.detail.resource?.id);
  };

  /**
   * Prefer the price block that matches this instance so PDP installments
   * are not skipped when the fetched HTML contains other product-price nodes.
   * @param {Document} html
   * @returns {Element | null}
   */
  #getMatchingProductPrice(html) {
    if (this.dataset.blockId) {
      const match = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
      if (match) return match;
    }

    return html.querySelector('product-price');
  }

  /**
   * Shop Pay Installments lives in a second product form next to the price.
   * Shopify only recalculates the banner when that form's `id` input changes.
   * @param {string | number | undefined} variantId
   */
  #updateInstallments(variantId) {
    const nextId = variantId != null ? String(variantId) : '';
    const installmentsInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`) ||
      this.querySelector('form.payment-terms input[name="id"]')
    );

    if (installmentsInput && installmentsInput.value !== nextId) {
      installmentsInput.value = nextId;
      installmentsInput.setAttribute('value', nextId);
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const paymentTerms = this.querySelector('shopify-payment-terms');
    if (paymentTerms && nextId) {
      paymentTerms.setAttribute('variant-id', nextId);
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
